
import React from "react";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";
import { Languages, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useIsMobile } from "@/hooks/use-mobile";

// Language code to label/flag
const LANGUAGES = [
  { code: "en", label: "English", flag: "🇬🇧" },
  { code: "vi", label: "Tiếng Việt", flag: "🇻🇳" },
  { code: "zh", label: "中文", flag: "🇨🇳" },
  { code: "th", label: "ไทย", flag: "🇹🇭" },
  { code: "ko", label: "한국어", flag: "🇰🇷" },
  { code: "ja", label: "日本語", flag: "🇯🇵" }
];

interface TopbarLanguageDropdownProps {
  buttonVariant?: 'ghost' | 'menuItem';
}

export const TopbarLanguageDropdown: React.FC<TopbarLanguageDropdownProps> = ({ buttonVariant = 'ghost' }) => {
  const { language, setLanguage, isLoading } = useLanguage();
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  
  // Don't render on mobile as it will be in the profile dropdown
  if (isMobile && buttonVariant !== 'menuItem') return null;
  
  // Find current language info
  const currentLang = LANGUAGES.find(l => l.code === language) || LANGUAGES[0];

  const handleLanguageChange = (langCode: string) => {
    console.log(`🎯 User manually selected language: ${langCode}`);
    setLanguage(langCode, true); // true = manual selection
  };

  // If it's a menu item, render a simpler version
  if (buttonVariant === 'menuItem') {
    return (
      <div className="flex items-center w-full" onClick={(e) => e.stopPropagation()}>
        <span className="text-lg mr-2">{isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : currentLang.flag}</span>
        <span>{t("change_language") || "Change language"}</span>
        <DropdownMenu>
          <DropdownMenuTrigger className="flex-1 flex justify-end" disabled={isLoading}>
            <Languages size={16} />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="min-w-36 z-[1200] bg-background">
            {LANGUAGES.map((lang) => (
              <DropdownMenuItem
                key={lang.code}
                onClick={() => handleLanguageChange(lang.code)}
                className={language === lang.code ? "bg-accent text-accent-foreground font-semibold" : ""}
                disabled={isLoading}
              >
                <span className="mr-2 text-lg">{lang.flag}</span>
                <span>{lang.label}</span>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          aria-label={t("change_language") || "Change language"}
          className="relative h-8 gap-1 px-2"
          disabled={isLoading}
        >
          {isLoading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <>
              <span className="text-lg mr-1">{currentLang.flag}</span>
              <Languages size={16} />
            </>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-36 z-[1200] bg-background">
        {LANGUAGES.map((lang) => (
          <DropdownMenuItem
            key={lang.code}
            onClick={() => handleLanguageChange(lang.code)}
            className={language === lang.code ? "bg-accent text-accent-foreground font-semibold" : ""}
            disabled={isLoading}
          >
            <span className="mr-2 text-lg">{lang.flag}</span>
            <span>{lang.label}</span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
